import { resolvePageName } from "./resolvePageName";

const DIALOG_TYPE = "dialog";
const ACCORDION_TYPE = "accordion";
const SORT_TYPE = "sort";
const ARIA_LABEL_ATTRIBUTE = "aria-label";

const ELEMENT_TYPES = new Set([
  "button",
  "link",
  "menu_item",
  "tab",
  "select",
  "search_input",
  "checkbox",
  "radio",
  "toggle",
  "pagination",
  SORT_TYPE,
  DIALOG_TYPE,
  ACCORDION_TYPE,
  "table_row",
  "action_icon",
  "dashboard",
  "dashboard_card",
  "benchmark",
  "item",
  "input",
  "document",
  "scroll_container",
  "chatbot",
]);

const TYPE_ALIASES = {
  a: "link",
  menuitem: "menu_item",
  switch: "toggle",
  "select-one": "select",
  "select-multiple": "select",
  text: "input",
  search: "search_input",
  tr: "table_row",
};

// A user gesture stays "active" briefly after dispatch so DOM side effects of the
// same click (popup/accordion opening, re-renders) are not reported a second time.
export const INTERACTION_WINDOW_MS = 1000;

let activeInteraction = null;

const getActiveInteraction = () =>
  activeInteraction &&
  Date.now() - activeInteraction.startedAt <= INTERACTION_WINDOW_MS
    ? activeInteraction
    : null;

export const beginInteraction = (target, event) => {
  const interaction = {
    target,
    event,
    claimed: false,
    dispatching: true,
    startedAt: Date.now(),
  };
  activeInteraction = interaction;
  return interaction;
};

export const finishInteraction = (interaction) => {
  if (interaction) interaction.dispatching = false;
};

export const resetInteraction = () => {
  activeInteraction = null;
};

export const claimInteraction = () => {
  const interaction = getActiveInteraction();
  if (interaction) interaction.claimed = true;
};

export const hasActiveInteraction = () => Boolean(getActiveInteraction());

// GA4 truncates event parameter values at 100 characters.
export const toParamValue = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (Array.isArray(value)) {
    const values = value
      .map(toParamValue)
      .filter((entry) => entry !== undefined);
    return values.length ? values.join(", ").slice(0, 100) : undefined;
  }
  if (typeof value === "object") {
    const key = [
      "label",
      "name",
      "clientName",
      "title",
      "value",
      "code",
      "id",
    ].find(
      (field) => value[field] !== undefined && typeof value[field] !== "object",
    );
    return key ? toParamValue(value[key]) : undefined;
  }
  const text = String(value).replace(/\s+/g, " ").trim();
  return text ? text.slice(0, 100) : undefined;
};

// PrimeReact's onSort callback carries only the field, so the clicked header
// is remembered to report the column's visible title.
let lastSortHeader = null;
export const rememberSortHeader = (header) => {
  lastSortHeader = header ? { header, at: Date.now() } : null;
};
export const recentSortHeader = () =>
  lastSortHeader?.header.isConnected &&
  Date.now() - lastSortHeader.at <= INTERACTION_WINDOW_MS
    ? lastSortHeader.header
    : null;

// Lets the pending click of the current gesture report the popup/accordion state
// it caused (e.g. "popup_opened") instead of a generic "clicked".
export const attachStateChange = (change) => {
  const interaction = getActiveInteraction();
  if (
    !interaction?.dispatching ||
    interaction.claimed ||
    interaction.stateChange
  )
    return;
  interaction.stateChange = change;
};

export const toAnalyticsId = (value = "") => {
  const text = String(value);
  if (/@|\d{6,}/.test(text)) return "";

  const normalized = text
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
  let start = 0;
  let end = normalized.length;
  while (normalized[start] === "_") start += 1;
  while (end > start && normalized[end - 1] === "_") end -= 1;

  return normalized.slice(start, end).slice(0, 100);
};

const safeLabel = (value) =>
  typeof value === "string" && !/[@\r\n]/.test(value)
    ? value.trim().slice(0, 100)
    : "";

const textOf = (node) => {
  const text = node?.textContent?.replace(/\s+/g, " ").trim() || "";
  return text.length <= 60 ? text : "";
};

const labelledBy = (element) =>
  (element?.getAttribute?.("aria-labelledby") || "")
    .split(/\s+/)
    .map((id) => id && document.getElementById(id))
    .filter(Boolean)
    .map(textOf)
    .join(" ")
    .trim();

const formLabel = (element) => {
  const input = element?.matches?.("input, select, textarea")
    ? element
    : element?.querySelector?.("input, select, textarea");
  const id = input?.id;
  const label =
    (id &&
      Array.from(document.getElementsByTagName("label")).find(
        (node) => node.htmlFor === id,
      )) ||
    element?.closest?.("label");
  return (
    labelledBy(input) ||
    textOf(label) ||
    input?.getAttribute?.("placeholder") ||
    element?.getAttribute?.("placeholder") ||
    input?.getAttribute?.("name") ||
    ""
  );
};

const POPUP_ROOTS =
  "[role='dialog'], .modal, .p-dialog, .p-sidebar, .p-overlaypanel, .dropdown-menu, [role='listbox']";

// Developer-authored UI copy only (titles, headers, form labels, placeholders).
// Data cells, rows and options are dynamic content and are never read.
const structuralLabel = (element, type) => {
  if (!element?.matches || element.closest("tbody, [role='option']")) return "";
  if (type === DIALOG_TYPE) {
    const root = element.closest(POPUP_ROOTS) || element;
    const owner =
      root.id &&
      document.querySelector(
        `[aria-controls="${root.id}"], [aria-owns="${root.id}"]`,
      );
    return (
      safeLabel(root.getAttribute(ARIA_LABEL_ATTRIBUTE)) ||
      labelledBy(root) ||
      textOf(
        root.querySelector(
          ".p-dialog-title, .modal-title, .p-sidebar-header h1, .p-sidebar-header h2, .p-sidebar-header h3, h1, h2, h3, h4",
        ),
      ) ||
      (owner && (owner.dataset.analyticsLabel || formLabel(owner))) ||
      ""
    );
  }
  if (type === ACCORDION_TYPE) {
    if (element.matches("details"))
      return textOf(element.querySelector("summary"));
    const header =
      element.closest(".p-accordion-header, .accordion-header") ||
      element
        .closest(".p-accordion-tab")
        ?.querySelector(".p-accordion-header") ||
      (element.matches(".accordion-collapse") &&
        element.previousElementSibling?.matches?.(".accordion-header") &&
        element.previousElementSibling);
    return (
      labelledBy(element) ||
      textOf(
        header?.querySelector?.(
          ".p-accordion-header-text, .accordion-button",
        ) || header,
      )
    );
  }
  if (type === SORT_TYPE) {
    const header = element.closest("th, [role='columnheader']") || element;
    return textOf(header.querySelector(".p-column-title") || header);
  }
  if (
    ["search_input", "input", "select", "checkbox", "radio", "toggle"].includes(
      type,
    )
  )
    return formLabel(element);
  return "";
};

export const toNormalCase = (value = "") => {
  if (!value) return "";
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[_\s-]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
    .slice(0, 100)
    .trim();
};

export const readableLabel = toNormalCase;

const labelFromAnalyticsId = (value) =>
  String(value)
    .split("_")
    .map((part) => (part === MISSING_PART ? MISSING_PART : toNormalCase(part)))
    .join(" ");

export const getInteractionPrefix = (overrides = {}) => {
  const path = overrides.page_path || globalThis.location?.pathname || "";
  const pageId = toAnalyticsId(resolvePageName(path) || "") || MISSING_PART;
  return [pageId];
};

const MISSING_PART = "-";

// element_id: <page>_<label>_<id>
// element_label: the same parts in Normal Case. Missing labels are omitted.
export const buildElementMetadata = ({ prefix = [], id, label }) => {
  const parts = [...prefix, ...(toAnalyticsId(label) ? [label] : []), id];
  return {
    element_id: parts
      .map((part) => toAnalyticsId(part) || MISSING_PART)
      .join("_")
      .slice(0, 100),
    element_label: parts
      .map((part) => toNormalCase(part) || MISSING_PART)
      .join(" ")
      .slice(0, 100),
  };
};

// Visible name of a control from its UI copy (column title, form label, header).
export const getControlLabel = (element, type) =>
  safeLabel(
    element?.dataset?.analyticsLabel ||
      element?.getAttribute?.(ARIA_LABEL_ATTRIBUTE) ||
      structuralLabel(element, type || getElementType(element)),
  );

const stableDomId = (element) => {
  const id = element?.getAttribute?.("id") || "";
  // React/PrimeReact IDs describe a mount, not a reporting identity.
  const isGeneratedId = /^(?:pr_id|react|rc-|:)/i.test(id);
  const containsNumericId = /\d{6,}/.test(id);
  return isGeneratedId || containsNumericId ? "" : toAnalyticsId(id);
};

const getStructuralElementType = (element) => {
  if (element.matches("[role='dialog'], .modal, .p-dialog"))
    return DIALOG_TYPE;
  if (
    element.matches(".accordion-collapse, .p-accordion-content, details") ||
    element.closest(".accordion-header, .p-accordion-header")
  )
    return ACCORDION_TYPE;
  if (element.closest(".p-paginator, .pagination")) return "pagination";
  if (element.matches("th[aria-sort], .p-sortable-column")) return SORT_TYPE;
  if (element.matches("tr, [role='row']")) return "table_row";
  return "";
};

const getFormElementType = (element) => {
  if (
    element.matches(
      "button[type='submit'], input[type='submit'], input[type='button'], input[type='reset']",
    )
  )
    return "button";
  if (
    element.matches(
      "input[type='search'], .p-dropdown-filter, .p-multiselect-filter, [data-analytics-type='search_input']",
    ) ||
    (element.matches("input") &&
      /search|find/i.test(
        `${element.id} ${element.name} ${element.getAttribute("placeholder") || ""}`,
      ))
  )
    return "search_input";
  if (element.matches("input[type='checkbox']"))
    return element.closest(".p-inputswitch") ? "toggle" : "checkbox";
  if (element.matches("input[type='radio']")) return "radio";
  if (element.matches("[role='checkbox']") || element.closest(".p-checkbox"))
    return "checkbox";
  if (element.matches("[role='radio']") || element.closest(".p-radiobutton"))
    return "radio";
  if (element.matches("[role='switch']") || element.closest(".p-inputswitch"))
    return "toggle";
  if (element.matches("[role='option'], .p-dropdown-item, .p-multiselect-item"))
    return "select";
  if (
    element.matches("[role='combobox'], select") ||
    element.closest(".p-dropdown, .p-multiselect")
  )
    return "select";
  return "";
};

const getNavigationElementType = (element) => {
  if (element.matches("[role='menuitem']")) return "menu_item";
  if (element.matches("[role='tab']")) return "tab";
  if (element.matches("a")) return "link";
  return "";
};

const getTextInputElementType = (element) =>
  element.matches("input, textarea") ? "input" : "";

const getIconElementType = (element) =>
  element.matches("svg, img, i") || element.querySelector("svg, img, i")
    ? "action_icon"
    : "";

export const getElementType = (element) => {
  const explicit = element?.dataset?.analyticsType;
  if (ELEMENT_TYPES.has(explicit)) return explicit;
  if (!element?.matches) return "button";

  return (
    getStructuralElementType(element) ||
    getFormElementType(element) ||
    getNavigationElementType(element) ||
    getTextInputElementType(element) ||
    getIconElementType(element) ||
    "button"
  );
};

const inferType = (params) => {
  const action = params.interaction_type || "";
  if (/popup|dialog/.test(action)) return DIALOG_TYPE;
  if (/accordion/.test(action)) return ACCORDION_TYPE;
  if (/search/.test(action)) return "search_input";
  if (/sort/.test(action)) return SORT_TYPE;
  if (/page_changed|page_size/.test(action)) return "pagination";
  if (/toggle/.test(action)) return "toggle";
  if (/scroll/.test(action)) return "scroll_container";
  if (/copied|pasted|printed/.test(action)) return "document";
  if (/chatbot/.test(action)) return "chatbot";
  if (/card/.test(action)) return "dashboard_card";
  return ELEMENT_TYPES.has(params.content_type)
    ? params.content_type
    : "button";
};

export const getElementMetadata = (element, overrides = {}) => {
  const rawType = overrides.element_type || getElementType(element);
  const elementType = TYPE_ALIASES[rawType] || rawType;
  const type = ELEMENT_TYPES.has(elementType) ? elementType : "button";
  if (type === ACCORDION_TYPE) {
    element =
      element?.closest?.(".accordion-item[data-analytics-id]") || element;
  }
  const dynamicContent =
    type === "table_row" ||
    element?.matches?.(
      "[role='option'], .p-dropdown-item, .p-multiselect-item",
    );
  const explicitLabel = safeLabel(
    overrides.element_label ||
      element?.dataset?.analyticsLabel ||
      (!dynamicContent && element?.getAttribute?.(ARIA_LABEL_ATTRIBUTE)) ||
      (!dynamicContent && element?.getAttribute?.("title")) ||
      (!dynamicContent && structuralLabel(element, type)),
  );
  const domId =
    toAnalyticsId(overrides.element_id || element?.dataset?.analyticsId) ||
    (!dynamicContent && stableDomId(element)) ||
    "";

  if (type === "dashboard" || overrides.content_type === "dashboard") {
    const dashboardContentId =
      domId ||
      `dashboard_${toAnalyticsId(overrides.dashboard || "bank_performance_metrics")}`;
    const dashboardLabel = explicitLabel || toNormalCase(dashboardContentId);
    return {
      element_id: dashboardContentId,
      element_type: "dashboard",
      element_label: dashboardLabel,
    };
  }

  const prefixParts = getInteractionPrefix(overrides);
  const prefix = prefixParts.join("_");
  const builtId = String(overrides.element_id || "");
  if (builtId.includes("_page_")) {
    return {
      element_id: builtId,
      element_label: labelFromAnalyticsId(builtId),
      element_type: type,
    };
  }
  // Already-built IDs are normalized again at the service boundary.
  if (builtId.startsWith(`${prefix || MISSING_PART}_`)) {
    return {
      element_id: builtId,
      element_label: labelFromAnalyticsId(builtId),
      element_type: type,
    };
  }
  return {
    ...buildElementMetadata({
      prefix: prefixParts,
      id: domId,
      label: explicitLabel,
    }),
    element_type: type,
  };
};

export const normalizeInteractionParams = (params = {}) => {
  const target = activeInteraction?.dispatching
    ? activeInteraction.target
    : undefined;
  const inferredType = ELEMENT_TYPES.has(params.content_type)
    ? params.content_type
    : target
      ? getElementType(target)
      : inferType(params);
  const contentId =
    params.content_type === "dashboard"
      ? `dashboard_${toAnalyticsId(params.dashboard || "bank_performance_metrics")}`
      : "";
  const metadata = getElementMetadata(target, {
    ...params,
    element_type:
      params.element_type ||
      (target ? inferredType : params.input_type) ||
      inferredType,
    element_id: params.element_id || params.input_name || contentId,
  });
  return { ...params, ...metadata };
};
